import './registry';
import './region';
import { registerBlock } from './register-block';
import { RelaunchHeader } from './header';
import { ArticleVideo } from './video';

const fold = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();

class Accordions {
  constructor(scope, options = {}) {
    this.scope = scope;
    this.listeners = [];
    this.animations = new WeakMap();
    this.groups = [...scope.querySelectorAll('.amt-accordion')];
    this.groups.forEach((group, index) => {
      const button = group.querySelector('[data-toggle]');
      this.setOpen(group, !!options.firstOpen && index === 0);
      this.on(button, 'click', () => this.toggle(group));
    });
  }
  on(target, event, handler) {
    if (!target) return;
    target.addEventListener(event, handler);
    this.listeners.push(() => target.removeEventListener(event, handler));
  }
  setOpen(group, open) {
    const panel = group.querySelector('[data-panel]');
    this.cancelAnimation(panel);
    panel.style.removeProperty('overflow');
    group.querySelector('[data-toggle]').setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    group.querySelector('.amt-accordion__marker').textContent = open ? '−' : '+';
  }
  cancelAnimation(panel) {
    // Enumerating all animations flushes pending styles even on initial setup.
    // These panels only need to cancel the transition this instance started.
    const animation = this.animations.get(panel);
    if (animation) {
      this.animations.delete(panel);
      animation.cancel();
    }
  }
  toggle(group) {
    const open = group.querySelector('[data-toggle]').getAttribute('aria-expanded') !== 'true';
    if (open) this.groups.forEach((item) => this.setOpenAnimated(item, item === group));
    else this.setOpenAnimated(group, false);
  }
  setOpenAnimated(group, open) {
    const button = group.querySelector('[data-toggle]');
    const panel = group.querySelector('[data-panel]');
    const marker = group.querySelector('.amt-accordion__marker');
    const wasHidden = panel.hidden;
    const currentStyle = getComputedStyle(panel);
    const current = {
      height: wasHidden ? 0 : panel.getBoundingClientRect().height,
      opacity: wasHidden ? 0 : Number.parseFloat(currentStyle.opacity),
      paddingTop: wasHidden ? '0px' : currentStyle.paddingTop,
      paddingBottom: wasHidden ? '0px' : currentStyle.paddingBottom,
      borderTopWidth: wasHidden ? '0px' : currentStyle.borderTopWidth,
      borderBottomWidth: wasHidden ? '0px' : currentStyle.borderBottomWidth,
    };

    this.cancelAnimation(panel);
    panel.style.removeProperty('overflow');
    button.setAttribute('aria-expanded', String(open));
    marker.textContent = open ? '−' : '+';

    if (!open && panel.hidden) return;

    if (!panel.animate || panel.ownerDocument.defaultView.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.hidden = !open;
      return;
    }

    if (open) panel.hidden = false;
    panel.style.overflow = 'hidden';
    const expandedStyle = getComputedStyle(panel);
    const targetHeight = open ? panel.getBoundingClientRect().height : 0;
    const animation = panel.animate([
      { ...current, height: `${current.height}px` },
      {
        height: `${targetHeight}px`,
        opacity: open ? 1 : 0,
        paddingTop: open ? expandedStyle.paddingTop : '0px',
        paddingBottom: open ? expandedStyle.paddingBottom : '0px',
        borderTopWidth: open ? expandedStyle.borderTopWidth : '0px',
        borderBottomWidth: open ? expandedStyle.borderBottomWidth : '0px',
      },
    ], {
      duration: 280,
      easing: 'cubic-bezier(.22, .75, .2, 1)',
      fill: 'both',
    });
    this.animations.set(panel, animation);

    animation.onfinish = () => {
      if (this.animations.get(panel) !== animation) return;
      panel.hidden = !open;
      panel.style.removeProperty('overflow');
      this.cancelAnimation(panel);
    };
  }
  destroy() {
    this.listeners.forEach((remove) => remove());
    this.groups.forEach((group) => this.setOpen(group, true));
  }
}

class Library extends Accordions {
  constructor(scope) {
    super(scope);
    this.search = scope.querySelector('[data-search]');
    this.searchForm = scope.querySelector('[data-search-form]');
    this.query = '';
    this.hasSubmitted = false;
    this.location = scope.querySelector('[data-location]');
    this.locationNative = scope.querySelector('[data-location-native]');
    this.locationPicker = scope.querySelector('[data-location-picker]');
    this.locationTrigger = scope.querySelector('[data-location-trigger]');
    this.locationPanel = scope.querySelector('[data-location-panel]');
    this.locationList = scope.querySelector('[data-location-list]');
    this.locationOptions = [...scope.querySelectorAll('[data-location-option]')];
    this.locationTypeahead = '';
    this.locationTypeaheadTimer = 0;
    this.topics = [...scope.querySelectorAll('[data-topic]')];
    this.topic = 'all';
    this.menuGuides = null;
    this.menuLabel = '';
    this.selectionLinks = [...scope.ownerDocument.querySelectorAll('a[data-amt-menu-selection][data-amt-guide-ids]')];
    this.regionRows = [...scope.querySelectorAll('[data-region-row]')];
    this.regionRows.forEach((row) => this.on(row.querySelector('summary'), 'click', () => {
      // Native summary activation handles pointer and keyboard input. Close
      // siblings only when the visitor opens a region, not when filters expose results.
      if (!row.open) this.regionRows.forEach((other) => {
        if (other !== row) other.open = false;
      });
    }));
    this.guides = [...scope.querySelectorAll('[data-guide]')].map((element) => ({
      element,
      text: fold(`${element.closest('[data-group]').querySelector('.amt-accordion__title').textContent} ${element.querySelector('.amt-guide__label').textContent} ${element.querySelector('.amt-guide__excerpt')?.textContent || ''}`),
      region: element.dataset.region,
      group: element.closest('[data-group]').dataset.group,
    }));
    const win = scope.ownerDocument.defaultView;
    let pendingRegion = null;
    const fallbackRegion = win.location.hash.match(/^#library-region-([a-z]+)$/)?.[1] || null;
    if (win.location.hash === '#library') {
      try {
        pendingRegion = win.sessionStorage.getItem('amt:pending-library-region');
        win.sessionStorage.removeItem('amt:pending-library-region');
      } catch (error) { /* Storage can be disabled. */ }
    }
    if (!pendingRegion) pendingRegion = fallbackRegion;
    if ([...this.location.options].some((option) => option.value === pendingRegion)) {
      this.location.value = pendingRegion;
      this.topic = 'local';
    }
    else pendingRegion = null;
    if (fallbackRegion) win.history.replaceState(null, '', '#library');
    this.enhanceLocationPicker();
    scope.querySelector('[data-controls]').hidden = false;
    this.on(this.searchForm, 'submit', (event) => {
      event.preventDefault();
      this.submitSearch();
    });
    this.on(this.location, 'change', () => this.commitLocation(this.location.value));
    this.on(scope.ownerDocument.defaultView, 'amt:region-change', (event) => {
      const region = event.detail?.region;
      if (![...this.location.options].some((option) => option.value === region)) return;
      if (event.detail?.source === 'header') {
        this.applyRegionalSelection(region, { resetSearch: true });
        const win = this.scope.ownerDocument.defaultView;
        if (win.location.hash !== '#library') win.history.pushState(null, '', '#library');
        this.showRegionalResults(true);
        return;
      }
      if (this.location.value !== region) {
        this.location.value = region;
        this.syncLocationPicker();
        this.filter();
      }
    });
    this.topics.forEach((button) => this.on(button, 'click', () => {
      this.clearSelectionHash();
      if (button.dataset.topic === 'all') {
        this.reset();
        return;
      }
      this.menuGuides = null;
      this.menuLabel = '';
      this.topic = button.dataset.topic;
      this.filter();
    }));
    this.on(scope.querySelector('[data-clear]'), 'click', () => {
      this.clearSelectionHash();
      this.reset();
      this.search.focus();
    });
    // Anchors from the hero and FAQ also reveal the relevant group.
    this.on(scope.ownerDocument, 'click', (event) => {
      if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const selection = event.target.closest('a[data-amt-menu-selection][data-amt-guide-ids]');
      if (selection && selection.target !== '_blank') {
        event.preventDefault();
        const hash = `#${selection.dataset.amtMenuSelection}`;
        const win = this.scope.ownerDocument.defaultView;
        // Only the public menu destination enters history, never a search or location.
        if (win.location.hash !== hash) win.history.pushState(null, '', hash);
        this.reveal(hash, true);
        return;
      }
      const link = event.target.closest('a[href="#local"],a[href="#understand"],a[href="#library"],a[href="#heal"]');
      if (link) this.reveal(link.getAttribute('href'));
    });
    this.on(scope.ownerDocument.defaultView, 'hashchange', () => this.reveal(scope.ownerDocument.defaultView.location.hash));
    this.filter();
    if (pendingRegion) {
      const showPendingRegion = () => win.requestAnimationFrame(() => this.showRegionalResults(true));
      this.showRegionalResults(true);
      // Browsers may apply the #library anchor after deferred scripts run. Move
      // to the affected cards once more after load so the native anchor cannot
      // leave the actual regional results below the viewport.
      if (scope.ownerDocument.readyState === 'complete') showPendingRegion();
      else this.on(win, 'load', showPendingRegion);
    } else this.reveal(scope.ownerDocument.defaultView.location.hash);
    this.ready = true;
  }
  enhanceLocationPicker() {
    if (!this.locationNative || !this.locationPicker || !this.locationTrigger || !this.locationPanel || !this.locationList || !this.locationOptions.length) return;
    // Keep the native select as the single source of truth. Once the enhanced
    // control is ready, remove the duplicate native control from the a11y tree.
    this.locationPicker.hidden = false;
    this.locationNative.hidden = true;
    this.syncLocationPicker();
    this.on(this.locationTrigger, 'click', () => this.setLocationPickerOpen(this.locationPanel.hidden));
    this.on(this.locationTrigger, 'keydown', (event) => {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      this.setLocationPickerOpen(true, event.key === 'ArrowUp' ? 'last' : 'selected');
    });
    this.locationOptions.forEach((option) => {
      this.on(option, 'click', () => this.commitLocation(option.dataset.locationOption, true));
      this.on(option, 'keydown', (event) => this.onLocationOptionKeydown(event, option));
    });
    this.on(this.scope.ownerDocument, 'click', (event) => {
      if (!this.locationPicker.contains(event.target)) this.setLocationPickerOpen(false);
    });
    this.on(this.scope.ownerDocument.defaultView, 'resize', () => this.setLocationPickerOpen(false));
  }
  syncLocationPicker() {
    if (!this.locationTrigger || !this.locationOptions.length) return;
    const nativeOption = [...this.location.options].find((option) => option.value === this.location.value);
    const label = nativeOption?.textContent.trim() || this.location.value.toUpperCase();
    this.locationTrigger.querySelector('[data-location-trigger-label]').textContent = label;
    this.locationTrigger.setAttribute('aria-label', `${this.locationTrigger.dataset.labelPrefix}: ${label}`);
    this.locationOptions.forEach((option) => {
      const selected = option.dataset.locationOption === this.location.value;
      option.setAttribute('aria-selected', String(selected));
      option.setAttribute('tabindex', selected ? '0' : '-1');
    });
  }
  setLocationPickerOpen(open, focusTarget = 'selected') {
    if (!this.locationPicker || !this.locationPanel || !this.locationTrigger) return;
    this.locationPanel.hidden = !open;
    this.locationTrigger.setAttribute('aria-expanded', String(open));
    const win = this.scope.ownerDocument.defaultView;
    if (!open) {
      win.clearTimeout(this.locationTypeaheadTimer);
      this.locationTypeahead = '';
      delete this.locationPicker.dataset.placement;
      this.locationPanel.style.removeProperty('--amt-location-list-max');
      this.syncLocationPicker();
      return;
    }
    const rect = this.locationTrigger.getBoundingClientRect();
    const viewportHeight = win.visualViewport?.height || win.innerHeight;
    const below = Math.max(0, viewportHeight - rect.bottom - 12);
    const above = Math.max(0, rect.top - 12);
    const placeAbove = below < 280 && above > below;
    this.locationPicker.dataset.placement = placeAbove ? 'above' : 'below';
    const available = Math.max(180, Math.min(560, (placeAbove ? above : below) - 8));
    this.locationPanel.style.setProperty('--amt-location-list-max', `${Math.floor(available)}px`);
    const selected = this.locationOptions.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const index = focusTarget === 'last' ? this.locationOptions.length - 1 : Math.max(0, selected);
    this.focusLocationOption(index);
  }
  focusLocationOption(index) {
    if (!this.locationOptions.length) return;
    const normalized = (index + this.locationOptions.length) % this.locationOptions.length;
    const option = this.locationOptions[normalized];
    this.locationOptions.forEach((item, itemIndex) => item.setAttribute('tabindex', itemIndex === normalized ? '0' : '-1'));
    option.focus({ preventScroll: true });
    option.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  }
  onLocationOptionKeydown(event, option) {
    const index = this.locationOptions.indexOf(option);
    if (event.key === 'Escape') {
      event.preventDefault();
      this.setLocationPickerOpen(false);
      this.locationTrigger.focus({ preventScroll: true });
      return;
    }
    if (event.key === 'Tab') {
      // Restore the trigger before hiding the focused option. Native Tab then
      // continues outside the picker rather than starting again at the document.
      this.setLocationPickerOpen(false);
      this.locationTrigger.focus({ preventScroll: true });
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.commitLocation(option.dataset.locationOption, true);
      return;
    }
    const target = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: this.locationOptions.length - 1 }[event.key];
    if (target !== undefined) {
      event.preventDefault();
      this.focusLocationOption(target);
      return;
    }
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    event.preventDefault();
    const win = this.scope.ownerDocument.defaultView;
    win.clearTimeout(this.locationTypeaheadTimer);
    this.locationTypeahead += fold(event.key);
    const ordered = [...this.locationOptions.slice(index + 1), ...this.locationOptions.slice(0, index + 1)];
    const match = ordered.find((item) => fold(item.textContent.trim()).startsWith(this.locationTypeahead));
    if (match) this.focusLocationOption(this.locationOptions.indexOf(match));
    this.locationTypeaheadTimer = win.setTimeout(() => { this.locationTypeahead = ''; }, 700);
  }
  commitLocation(region, restoreFocus = false) {
    if (![...this.location.options].some((option) => option.value === region)) return;
    this.applyRegionalSelection(region);
    if (region !== 'all') this.rememberRegion(region);
    this.setLocationPickerOpen(false);
    if (restoreFocus) this.locationTrigger?.focus({ preventScroll: true });
  }
  applyRegionalSelection(region, { resetSearch = false } = {}) {
    // A jurisdiction only changes the legal guides. Treat choosing it as
    // navigation to that topic so the visitor immediately sees the content
    // that actually changed instead of 44 unchanged general guides.
    this.clearSelectionHash();
    if (resetSearch) {
      this.search.value = '';
      this.query = '';
      this.hasSubmitted = false;
    }
    this.location.value = region;
    this.topic = 'local';
    this.menuGuides = null;
    this.menuLabel = '';
    this.syncLocationPicker();
    this.filter();
  }
  showRegionalResults(focus = false) {
    const group = this.groups.find((item) => item.dataset.group === 'local');
    this.showGroupResults(group, focus);
  }
  showGroupResults(group, focus = false) {
    if (!group || group.hidden) return;
    this.setOpen(group, true);
    group.scrollIntoView({ block: 'start', behavior: 'auto' });
    if (focus) group.querySelector('[data-toggle]')?.focus({ preventScroll: true });
  }
  rememberRegion(region) {
    this.scope.ownerDocument.cookie = `WP_AFTERMETOO_REGION=${region}; Path=/; Max-Age=31536000; SameSite=Lax`;
    this.scope.ownerDocument.defaultView.dispatchEvent(new CustomEvent('amt:region-change', { detail: { region, source: 'library' } }));
  }
  submitSearch() {
    this.clearSelectionHash();
    this.menuGuides = null;
    this.menuLabel = '';
    this.query = this.search.value.trim();
    this.hasSubmitted = true;
    this.setLocationPickerOpen(false);
    // Focus reads the result heading once; live announcements are reserved for
    // filters that keep focus on their controls. Nothing is sent to the server.
    this.filter({ announce: false });
    const result = this.scope.querySelector('[data-result]');
    result.focus({ preventScroll: true });
    result.scrollIntoView({ block: 'start', behavior: 'auto' });
  }
  reset() {
    this.menuGuides = null;
    this.menuLabel = '';
    this.search.value = '';
    this.query = '';
    this.hasSubmitted = false;
    // The catalogue filter is temporary. The header's saved jurisdiction is a
    // separate reading preference, just as on the original homepage: its
    // regional links remain available while the catalogue shows all guides.
    this.location.value = 'all';
    this.topic = 'all';
    this.groups.forEach((group) => this.setOpen(group, false));
    this.syncLocationPicker();
    this.filter();
  }
  clearSelectionHash() {
    const win = this.scope.ownerDocument.defaultView;
    if (/^#library-selection-\d+$/.test(win.location.hash)) win.history.replaceState(null, '', '#library');
  }
  reveal(hash, focus = false) {
    // Resolve only selections rendered by this language's WordPress menu. The URL
    // cannot inject arbitrary guide IDs or carry a visitor's search between pages.
    const selection = this.selectionLinks.find((link) => `#${link.dataset.amtMenuSelection}` === hash);
    if (selection) {
      this.reset();
      this.menuGuides = new Set(selection.dataset.amtGuideIds.split(','));
      this.menuLabel = selection.textContent.trim().replace(/\s*→\s*$/u, '');
      this.filter();
      const group = this.groups.find((item) => !item.hidden && item.querySelector('[data-guide]:not([hidden])'));
      this.showGroupResults(group, focus);
      return;
    }
    if (hash === '#library' || hash === '#library-search' || hash === '#heal') {
      // Ordinary links to the catalogue, including the header search, must not
      // discard a visitor's current search, topic, or jurisdiction selection.
      if (!this.groups.some((group) => !group.hidden && group.querySelector('[data-toggle]').getAttribute('aria-expanded') === 'true')) {
        const firstVisible = this.groups.find((group) => !group.hidden);
        if (firstVisible) this.setOpen(firstVisible, true);
      }
      if (hash === '#library-search') {
        this.scope.scrollIntoView({ block: 'start', behavior: 'auto' });
        this.search.focus({ preventScroll: true });
      }
      return;
    }
    if (!['', '#local', '#understand'].includes(hash)) return;
    this.reset();
    if (!hash) return;
    const group = hash === '#local' ? this.groups.find((g) => g.dataset.group === 'local') : this.groups[0];
    if (group) this.setOpen(group, true);
  }
  filter({ announce = true } = {}) {
    // Only submitted text filters the catalogue. Draft typing stays untouched
    // when a topic or jurisdiction changes. Sensitive queries stay in memory:
    // no network requests, URL or storage writes.
    const words = fold(this.query).split(/\s+/).filter(Boolean);
    const region = this.location.value;
    const active = words.length > 0 || region !== 'all' || this.topic !== 'all' || this.menuGuides !== null;
    const openGroup = this.groups.find((group) => group.querySelector('[data-toggle]').getAttribute('aria-expanded') === 'true');
    let count = 0;
    this.guides.forEach((guide) => {
      const visible = words.every((word) => guide.text.includes(word))
        && (guide.group !== 'local' || region === 'all' || guide.region === region)
        && (this.topic === 'all' || guide.group === this.topic)
        && (!this.menuGuides || this.menuGuides.has(guide.element.dataset.guide));
      guide.element.hidden = !visible;
      if (visible) count++;
    });
    // Browsing starts with compact jurisdiction headings. Explicit filters and
    // curated menu destinations expose their matching questions immediately.
    this.updateRegionRows(region !== 'all' || words.length > 0 || this.menuGuides !== null);
    const firstVisibleGroup = this.groups.find((group) => group.querySelector('[data-guide]:not([hidden])'));
    const regionalGroup = region !== 'all'
      ? this.groups.find((group) => group.dataset.group === 'local')
      : null;
    const targetGroup = (words.length || this.topic !== 'all' || this.menuGuides)
      ? firstVisibleGroup
      : (regionalGroup || openGroup);
    let regionalCount = 0;
    this.groups.forEach((group) => {
      const visible = [...group.querySelectorAll('[data-guide]')].filter((guide) => !guide.hidden).length;
      if (group.dataset.group === 'local') regionalCount = visible;
      group.hidden = visible === 0;
      group.querySelector('[data-count]').textContent = visible;
      const countLabel = group.querySelector('[data-count-label]');
      if (countLabel) countLabel.textContent = visible === 1 ? countLabel.dataset.one : countLabel.dataset.many;
      const regionLabel = group.querySelector('[data-region-label]');
      if (regionLabel) {
        const location = this.location.options[this.location.selectedIndex].textContent;
        regionLabel.textContent = region === 'all' ? location : `${location} · ${regionLabel.dataset.totalLabel}`;
      }
      // Search results can span several topics. Opening every matching group is
      // the only way all results represented by the count are immediately visible.
      this.setOpen(group, words.length > 0 ? visible > 0 : group === targetGroup && visible > 0);
    });
    this.topics.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.topic === this.topic)));
    const result = this.scope.querySelector('[data-result]');
    const countTemplate = count === 1 && result.dataset.labelOne ? result.dataset.labelOne : result.dataset.label;
    const countLabel = countTemplate.replace('{count}', count);
    let resultParts = this.menuLabel ? [this.menuLabel, countLabel] : [countLabel];
    if (region !== 'all' && this.topic === 'local') {
      const location = this.location.options[this.location.selectedIndex].textContent;
      resultParts = [result.dataset.regionOnly.replace('{location}', location).replace('{count}', regionalCount)];
    } else if (region !== 'all') {
      const location = this.location.options[this.location.selectedIndex].textContent;
      resultParts.push(result.dataset.regionLocal.replace('{location}', location).replace('{count}', regionalCount));
      resultParts.push(result.dataset.regionGeneral.replace('{count}', count - regionalCount));
    }
    result.closest('.amt-library__result').hidden = !active && !this.hasSubmitted;
    result.textContent = resultParts.join(' · ');
    const announcement = this.scope.querySelector('[data-search-announcement]');
    if (announcement) announcement.textContent = announce && this.ready ? result.textContent : '';
    this.scope.querySelector('[data-empty]').hidden = count > 0;
    this.scope.querySelector('[data-clear]').hidden = !active;
  }
  updateRegionRows(expand) {
    this.regionRows.forEach((row) => {
      const count = row.querySelectorAll('[data-guide]:not([hidden])').length;
      row.hidden = count === 0;
      row.open = count > 0 && expand;
      row.querySelector('[data-region-count]').textContent = count;
      const label = row.querySelector('[data-region-count-label]');
      label.textContent = count === 1 ? label.dataset.one : label.dataset.many;
    });
  }
  destroy() {
    const win = this.scope.ownerDocument.defaultView;
    win.clearTimeout(this.locationTypeaheadTimer);
    this.setLocationPickerOpen(false);
    if (this.locationNative) this.locationNative.hidden = false;
    if (this.locationPicker) this.locationPicker.hidden = true;
    super.destroy();
    this.guides.forEach(({ element }) => { element.hidden = false; });
    this.updateRegionRows(false);
    this.groups.forEach((group) => { group.hidden = false; });
  }
}

registerBlock({ name: 'theme/amt-library', handler: Library });
registerBlock({ name: 'theme/amt-faq', handler: Accordions, params: { firstOpen: true } });
registerBlock({ name: 'theme/amt-navigation', selector: '.amt-header', handler: RelaunchHeader });
registerBlock({ name: 'theme/amt-article-video', selector: 'body.single-guide .amt-article-video', handler: ArticleVideo });

class GlossaryArchive {
  constructor(scope) {
    this.scope = scope;
    this.search = scope.querySelector('[data-glossary-search]');
    this.clearButtons = [...scope.querySelectorAll('[data-glossary-clear], [data-glossary-empty-clear]')];
    this.count = scope.querySelector('[data-glossary-count]');
    this.results = scope.querySelector('[data-glossary-results]');
    this.empty = scope.querySelector('[data-glossary-empty]');
    this.groups = [...scope.querySelectorAll('[data-glossary-group]')];
    this.letters = [...scope.querySelectorAll('[data-glossary-letter]')];
    this.listeners = [];
    this.items = [...scope.querySelectorAll('[data-glossary-item]')].map((element) => ({
      element,
      group: element.closest('[data-glossary-group]').dataset.glossaryGroup,
      text: fold(element.textContent),
    }));

    this.on(this.search, 'input', () => this.filter());
    this.clearButtons.forEach((button) => this.on(button, 'click', () => this.clear()));
    this.letters.forEach((letter) => this.on(letter, 'click', (event) => {
      if (letter.getAttribute('aria-disabled') === 'true') event.preventDefault();
    }));
  }

  on(target, event, handler) {
    if (!target) return;
    target.addEventListener(event, handler);
    this.listeners.push(() => target.removeEventListener(event, handler));
  }

  clear() {
    this.search.value = '';
    this.filter();
    this.search.focus();
  }

  filter() {
    const raw = this.search.value.trim();
    const query = fold(raw);
    let visibleCount = 0;
    const visibleGroups = new Set();

    this.items.forEach((item) => {
      const visible = !query || item.text.includes(query);
      item.element.hidden = !visible;
      if (visible) {
        visibleCount += 1;
        visibleGroups.add(item.group);
      }
    });

    this.groups.forEach((group) => {
      group.hidden = !visibleGroups.has(group.dataset.glossaryGroup);
    });

    this.letters.forEach((letter) => {
      const enabled = visibleGroups.has(letter.dataset.glossaryLetter);
      letter.setAttribute('aria-disabled', String(!enabled));
      if (enabled) letter.removeAttribute('tabindex');
      else letter.setAttribute('tabindex', '-1');
    });

    this.results.hidden = visibleCount === 0;
    this.empty.hidden = visibleCount !== 0;

    const template = !query
      ? this.count.dataset.default
      : (visibleCount === 1 ? this.count.dataset.single : this.count.dataset.many);
    this.count.textContent = template
      .replace('{count}', visibleCount)
      .replace('{query}', raw);
  }

  destroy() {
    this.listeners.forEach((remove) => remove());
    this.items.forEach((item) => { item.element.hidden = false; });
    this.groups.forEach((group) => { group.hidden = false; });
  }
}

registerBlock({ name: 'theme/amt-glossary', selector: '[data-amt-glossary]', handler: GlossaryArchive });

class GuideFeedback {
  constructor(scope) {
    this.scope = scope;
    this.actions = scope.querySelector('[data-feedback-actions]');
    this.status = scope.querySelector('[data-feedback-status]');
    this.error = scope.querySelector('[data-feedback-error]');
    this.buttons = [...scope.querySelectorAll('[data-feedback-value]')];
    this.listeners = [];
    this.buttons.forEach((button) => this.on(button, 'click', () => this.submit(button)));
  }
  on(target, event, handler) {
    target.addEventListener(event, handler);
    this.listeners.push(() => target.removeEventListener(event, handler));
  }
  async submit(button) {
    const endpoint = window.aftermetooData?.ajax_url;
    const nonce = this.scope.querySelector('#update_likes_nonce')?.value;
    if (!endpoint || !nonce) return;

    this.error.hidden = true;
    this.buttons.forEach((item) => { item.disabled = true; });
    const data = new FormData();
    data.append('action', 'update_likes');
    data.append('fn', button.dataset.feedbackValue);
    data.append('nonce', nonce);
    data.append('post_id', this.scope.dataset.postId);
    data.append('count', '1');

    try {
      const response = await fetch(endpoint, { method: 'POST', credentials: 'same-origin', body: data });
      if (!response.ok) throw new Error(`Feedback request failed: ${response.status}`);
      this.actions.hidden = true;
      this.status.hidden = false;
      this.status.focus({ preventScroll: true });
    } catch (error) {
      this.buttons.forEach((item) => { item.disabled = false; });
      this.error.hidden = false;
    }
  }
  destroy() { this.listeners.forEach((off) => off()); }
}

registerBlock({ name: 'theme/amt-guide-feedback', selector: '[data-amt-feedback]', handler: GuideFeedback });

registerBlock({ name: 'theme/amt-header', selector: '.amt-design .nav-bar', handler: (scope) => {
  const links = [...scope.querySelectorAll('[data-amt-exit]')];
  const leave = (event) => {
    event.preventDefault();
    scope.ownerDocument.defaultView.location.replace(event.currentTarget.href);
  };
  links.forEach((link) => link.addEventListener('click', leave));
  return { destroy() { links.forEach((link) => link.removeEventListener('click', leave)); } };
} });
